import { alpha, createTheme } from '@mui/material/styles';

const lightGreen = {
  500: '#8bc34a',
  600: '#7cb342',
  700: '#689f38',
  300: '#aed581',
};

const blue = {
  500: '#2196f3',
  600: '#1e88e5',
  700: '#1976d2',
  300: '#64b5f6',
  400: '#42a5f5',
};

const purple = {
  500: '#9c27b0',
  600: '#8e24aa',
  700: '#7b1fa2',
  300: '#ba68c8',
  400: '#ab47bc',
};

export function getTheme(userRole?: string, paletteMode: 'light' | 'dark' = 'dark') {
  const isAdmin = userRole === 'admin';
  const isDeveloper = userRole === 'developer';
  const isReferee = !isAdmin && !isDeveloper;
  const isDark = paletteMode === 'dark';

  /** Referee brand black — used for app bar in light mode and as brand accent in light UI. */
  const refereeBrandBlack = '#000000';

  /**
   * Semantic primary: visible text/links/chips in dark mode.
   * Referee app bar keeps brand black via TopBar, not palette.primary.main.
   */
  const primaryMain = isDeveloper
    ? purple[500]
    : isAdmin
      ? blue[500]
      : isDark
        ? 'rgba(255,255,255,0.92)'
        : refereeBrandBlack;

  const primaryContrastText =
    isDeveloper || isAdmin ? '#ffffff' : isDark ? '#0b0f14' : '#ffffff';

  const primaryHover = isDeveloper
    ? purple[600]
    : isAdmin
      ? blue[600]
      : isDark
        ? '#ffffff'
        : '#333333';

  const tableHeadBg = isDark ? 'rgba(255,255,255,0.06)' : '#fafafa';

  return createTheme({
    palette: {
      mode: paletteMode,
      primary: {
        main: primaryMain,
        contrastText: primaryContrastText,
        ...(isReferee ? { dark: refereeBrandBlack } : {}),
      },
      secondary: {
        main: isDeveloper ? purple[300] : isAdmin ? blue[300] : lightGreen[500],
        light: isDeveloper ? purple[300] : isAdmin ? blue[300] : lightGreen[300],
        dark: isDeveloper ? purple[700] : isAdmin ? blue[700] : lightGreen[700],
        contrastText: isDark ? '#ffffff' : '#000000',
      },
      background: isDark
        ? { default: '#0b0f14', paper: '#151b22' }
        : { default: '#f0f2f5', paper: '#ffffff' },
      text: isDark
        ? { primary: 'rgba(255,255,255,0.92)', secondary: 'rgba(255,255,255,0.65)', disabled: 'rgba(255,255,255,0.38)' }
        : { primary: '#1a1a1a', secondary: '#5f6368', disabled: 'rgba(0,0,0,0.38)' },
      divider: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      ...(isDark
        ? {
            action: {
              hover: 'rgba(255,255,255,0.06)',
              selected: 'rgba(255,255,255,0.10)',
              disabled: 'rgba(255,255,255,0.26)',
              disabledBackground: 'rgba(255,255,255,0.08)',
            },
          }
        : {}),
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h4: { fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle2: { fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: isDark
              ? isReferee
                ? `${alpha(lightGreen[500], 0.45)} #151b22`
                : 'rgba(255,255,255,0.28) #151b22'
              : `${alpha(lightGreen[500], 0.45)} #f0f2f5`,
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: isDark
              ? isReferee
                ? `${alpha(lightGreen[500], 0.45)} #151b22`
                : 'rgba(255,255,255,0.28) #151b22'
              : `${alpha(lightGreen[500], 0.45)} #f0f2f5`,
          },
          '*::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '*::-webkit-scrollbar-track': {
            background: isDark ? '#151b22' : '#f0f2f5',
          },
          '*::-webkit-scrollbar-thumb': {
            background: isDark
              ? isReferee
                ? alpha(lightGreen[500], 0.45)
                : 'rgba(255,255,255,0.22)'
              : alpha(lightGreen[500], 0.45),
            borderRadius: 4,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: isDark
              ? isReferee
                ? alpha(lightGreen[500], 0.65)
                : 'rgba(255,255,255,0.35)'
              : alpha(lightGreen[500], 0.65),
          },
          '*::-webkit-scrollbar-corner': {
            background: isDark ? '#151b22' : '#f0f2f5',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            fontWeight: 600,
          },
          containedPrimary:
            isReferee && isDark
              ? {
                  backgroundColor: lightGreen[600],
                  color: '#ffffff',
                  '&:hover': { backgroundColor: lightGreen[700] },
                }
              : {
                  backgroundColor: primaryMain,
                  color: primaryContrastText,
                  '&:hover': { backgroundColor: primaryHover },
                },
          outlined: ({ theme: t }) =>
            t.palette.mode === 'dark'
              ? {
                  borderColor: 'rgba(255,255,255,0.28)',
                  color: t.palette.text.primary,
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.45)',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                  },
                }
              : {},
          text: ({ theme: t }) =>
            t.palette.mode === 'dark'
              ? {
                  color: t.palette.text.primary,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                }
              : {},
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: isDark
              ? '0 2px 12px rgba(0,0,0,0.45)'
              : '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
            border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            mx: 1,
            mb: 0.25,
            '&.Mui-selected': {
              fontWeight: 600,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme: t }) =>
            t.palette.mode === 'dark'
              ? {
                  color: 'rgba(255,255,255,0.78)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                }
              : {},
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            color: t.palette.text.secondary,
            fontWeight: 600,
            '&.Mui-selected': {
              color: t.palette.text.primary,
            },
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: ({ theme: t }) => ({
            backgroundColor: t.palette.secondary.main,
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme: t }) =>
            t.palette.mode === 'dark'
              ? {
                  '&.MuiChip-outlinedDefault': {
                    borderColor: 'rgba(255,255,255,0.22)',
                    color: t.palette.text.primary,
                  },
                }
              : {},
          outlined: ({ theme: t }) =>
            t.palette.mode === 'dark'
              ? {
                  borderColor: 'rgba(255,255,255,0.22)',
                }
              : {},
          colorPrimary: ({ theme: t }) =>
            t.palette.mode === 'dark' && isReferee
              ? {
                  backgroundColor: alpha(t.palette.secondary.main, 0.22),
                  color: t.palette.secondary.light,
                }
              : {},
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderColor: t.palette.divider,
            color: t.palette.text.primary,
          }),
          head: {
            backgroundColor: tableHeadBg,
            fontWeight: 700,
          },
          stickyHeader: ({ theme: t }) => ({
            backgroundColor: tableHeadBg,
          }),
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            '&.MuiTableRow-hover:hover': {
              backgroundColor: t.palette.action.hover,
            },
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            color: t.palette.text.secondary,
            '&.Mui-focused': {
              color: t.palette.secondary.main,
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            color: t.palette.text.primary,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: t.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : undefined,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: t.palette.secondary.main,
            },
          }),
          input: ({ theme: t }) => ({
            color: t.palette.text.primary,
          }),
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: ({ theme: t }) => ({
            color: t.palette.text.secondary,
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            color: t.palette.text.primary,
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            color: t.palette.text.primary,
            borderColor: t.palette.divider,
            '&.Mui-selected': {
              color: t.palette.text.primary,
            },
          }),
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme: t }) =>
            t.palette.mode === 'dark'
              ? {
                  color: 'rgba(255,255,255,0.55)',
                  '&.Mui-checked': {
                    color: t.palette.secondary.main,
                  },
                }
              : {},
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: ({ theme: t }) => ({
            color: t.palette.text.primary,
          }),
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderColor: t.palette.divider,
          }),
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ theme: t }) =>
            t.palette.mode === 'dark'
              ? {
                  backgroundColor: alpha(t.palette.info.main, 0.12),
                  color: t.palette.text.primary,
                  '& .MuiAlert-icon': {
                    color: t.palette.info.light,
                  },
                }
              : {},
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme: t }) =>
            t.palette.mode === 'dark'
              ? {
                  backgroundColor: '#263238',
                  color: 'rgba(255,255,255,0.92)',
                }
              : {},
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ theme: t }) =>
            isReferee && t.palette.mode === 'dark'
              ? {
                  color: t.palette.secondary.light,
                }
              : {},
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          colorPrimary: ({ theme: t }) =>
            isReferee && t.palette.mode === 'dark'
              ? {
                  color: t.palette.text.primary,
                }
              : {},
        },
      },
    },
  });
}

export const theme = getTheme();
